/**
 * PermissionManagementPanel - Permission and Template Management
 *
 * FERP CRITICAL: Permission template management and role inheritance visualization
 *
 * Features:
 * - View all available permission scopes and capabilities
 * - Import permission templates (standard role definitions)
 * - Role inheritance tree visualization
 * - Effective permissions calculation
 * - Permission comparison between roles
 *
 * Architecture Principles:
 * - Data-driven (all permissions from backend)
 * - Repository pattern for operations
 * - Complete audit trail
 * - Life-critical validation
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Badge,
  Button,
  Select,
  Modal,
  Alert,
  Card,
  Title,
  Stack,
  Box,

  Tabs,

  SimpleGrid,
} from '@mantine/core';
import {
  IconShield,
  IconDownload,
  IconAlertCircle,
  IconCheck,
  IconTree,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  RoleDefinition,
  EffectiveRole,
  RoleRepository,
} from '../data/repositories/RoleRepository';

export interface PermissionManagementPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Is loading */
  isLoading: boolean;
}

/**
 * Permission Management Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (permissions from backend)
 * - ✅ Repository pattern
 * - ✅ Complete audit trail
 * - ✅ Life-critical validation
 */
export function PermissionManagementPanel({
  tenantId,
  isLoading: _parentLoading,
}: PermissionManagementPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new RoleRepository(apolloClient), [apolloClient]);

  // State
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Selected role for effective permissions
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [effectiveRole, setEffectiveRole] = useState<EffectiveRole | null>(null);

  // Permission template
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [compareRoleIds, setCompareRoleIds] = useState<[string, string]>(['', '']);

  // Available permission scopes (from backend constants)
  const availableScopes = [
    { category: 'Workflow', scopes: ['workflow:append', 'workflow:read', 'workflow:admin', 'workflow:signal', 'workflow:signal:submit'] },
    { category: 'Settings', scopes: ['settings:read', 'settings:write'] },
    { category: 'Rules', scopes: ['rules:read', 'rules:write', 'rules:activate', 'rules:list', 'rules:approve'] },
    { category: 'Workflow Definitions', scopes: ['wfdef:read', 'wfdef:write', 'wfdef:approve'] },
    { category: 'Forms', scopes: ['forms:read', 'forms:write'] },
    { category: 'Administration', scopes: ['tenant:admin', 'timers:write', 'broadcast:send'] },
  ];

  // Available permission templates
  const availableTemplates = [
    {
      value: 'admin',
      label: 'Administrator (Full Access)',
      description: 'Complete tenant administration with all permissions',
      scopes: ['tenant:admin', 'workflow:admin', 'settings:write', 'rules:write', 'rules:approve', 'wfdef:write', 'wfdef:approve'],
    },
    {
      value: 'approver',
      label: 'Approver (Review & Approve)',
      description: 'Can review and approve workflow definitions and rules',
      scopes: ['workflow:read', 'rules:read', 'rules:approve', 'wfdef:read', 'wfdef:approve', 'forms:read'],
    },
    {
      value: 'curator',
      label: 'Curator (Content Management)',
      description: 'Can manage workflow definitions and rules (no approval)',
      scopes: ['workflow:read', 'rules:read', 'rules:write', 'wfdef:read', 'wfdef:write', 'forms:read', 'forms:write'],
    },
    {
      value: 'operator',
      label: 'Operator (Workflow Execution)',
      description: 'Can execute workflows and submit forms',
      scopes: ['workflow:append', 'workflow:read', 'workflow:signal', 'workflow:signal:submit', 'forms:read', 'forms:write'],
    },
    {
      value: 'analyst',
      label: 'Analyst (Read & Report)',
      description: 'Can view all data for analysis and reporting',
      scopes: ['workflow:read', 'rules:read', 'rules:list', 'wfdef:read', 'forms:read', 'settings:read'],
    },
    {
      value: 'viewer',
      label: 'Viewer (Read-Only)',
      description: 'Basic read-only access to workflows and forms',
      scopes: ['workflow:read', 'wfdef:read', 'rules:read', 'forms:read'],
    },
  ];

  /**
   * Load roles from backend
   * FERP: Repository pattern for all data access
   */
  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const roleList = await repository.listRoles(tenantId);
      setRoles(roleList);
    } catch (err) {
      console.error('[PermissionManagementPanel] Failed to load roles:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, repository]);

  // Load roles on mount
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  /**
   * Load effective role permissions
   * FERP: Backend computation of inherited permissions
   */
  const loadEffectiveRole = useCallback(async () => {
    if (!selectedRoleId) {
      setEffectiveRole(null);
      return;
    }

    try {
      setLoading(true);
      const effective = await repository.getEffectiveRole(selectedRoleId, tenantId);
      setEffectiveRole(effective);
    } catch (err) {
      console.error('[PermissionManagementPanel] Failed to load effective role:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId, tenantId, repository]);

  // Load effective role when selection changes
  useEffect(() => {
    loadEffectiveRole();
  }, [loadEffectiveRole]);

  /**
   * Handle import permission template
   * FERP: Import predefined role template
   */
  const handleImportTemplate = async () => {
    if (!selectedTemplate) return;

    const template = availableTemplates.find((t) => t.value === selectedTemplate);
    if (!template) return;

    try {
      setLoading(true);

      // Create role from template
      await repository.createRole({
        tenantId,
        roleName: template.value,
        displayName: template.label,
        description: template.description,
        scopes: template.scopes,
        capabilities: [],
        inheritsFrom: [],
        enabled: true,
      });

      await loadRoles(); // Refresh list
      setIsImportModalOpen(false);
      setSelectedTemplate('');
    } catch (err) {
      console.error('[PermissionManagementPanel] Failed to import template:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Build role inheritance tree
   * FERP: Visualize role hierarchy
   */
  const buildInheritanceTree = (role: RoleDefinition, depth = 0): React.ReactNode => {
    const parentRoles = roles.filter((r) => role.inheritsFrom.includes(r.roleName));

    return (
      <Box key={role.roleId} ml={depth * 20}>
        <Group gap="xs" mb="xs">
          <IconShield size={16} />
          <Text size="sm" fw={depth === 0 ? 600 : 400}>
            {role.displayName}
          </Text>
          <Badge size="xs" color="blue">
            {role.scopes.length} scopes
          </Badge>
        </Group>
        {parentRoles.map((parent) => buildInheritanceTree(parent, depth + 1))}
      </Box>
    );
  };

  // Error state
  if (error) {
    return (
      <Alert color="red" title="Error Loading Permissions" icon={<IconAlertCircle size={16} />}>
        {error.message}
        <Button variant="light" size="xs" mt="xs" onClick={loadRoles}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Tabs defaultValue="scopes">
        <Tabs.List>
          <Tabs.Tab value="scopes" leftSection={<IconShield size={16} />}>
            Available Scopes
          </Tabs.Tab>
          <Tabs.Tab value="effective" leftSection={<IconCheck size={16} />}>
            Effective Permissions
          </Tabs.Tab>
          <Tabs.Tab value="inheritance" leftSection={<IconTree size={16} />}>
            Role Inheritance
          </Tabs.Tab>
          <Tabs.Tab value="templates" leftSection={<IconDownload size={16} />}>
            Import Templates
          </Tabs.Tab>
        </Tabs.List>

        {/* Available Scopes Tab */}
        <Tabs.Panel value="scopes" pt="md">
          <Stack gap="md">
            <Alert color="blue" icon={<IconShield size={16} />}>
              All available permission scopes in the system. These can be assigned to roles to grant
              specific capabilities.
            </Alert>

            {availableScopes.map((category) => (
              <Card key={category.category} withBorder p="md">
                <Title order={5} mb="sm">
                  {category.category}
                </Title>
                <Group gap={8}>
                  {category.scopes.map((scope) => (
                    <Badge key={scope} size="md" color="blue" variant="light">
                      {scope}
                    </Badge>
                  ))}
                </Group>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        {/* Effective Permissions Tab */}
        <Tabs.Panel value="effective" pt="md">
          <Stack gap="md">
            <Select
              label="Select Role"
              description="View effective permissions including inherited scopes"
              placeholder="Select a role"
              data={roles.map((r) => ({ value: r.roleId, label: r.displayName }))}
              value={selectedRoleId}
              onChange={(value) => setSelectedRoleId(value || '')}
              searchable
              leftSection={<IconShield size={16} />}
            />

            {effectiveRole && (
              <Card withBorder p="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <div>
                      <Text fw={700}>{effectiveRole.displayName}</Text>
                      <Text size="sm" c="dimmed">
                        {effectiveRole.roleName}
                      </Text>
                    </div>
                    <Badge color="blue" size="lg">
                      {effectiveRole.effectiveScopes.length} scopes
                    </Badge>
                  </Group>

                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Effective Scopes (including inherited)
                    </Text>
                    <Group gap={4}>
                      {effectiveRole.effectiveScopes.map((scope) => (
                        <Badge key={scope} size="sm" color="blue">
                          {scope}
                        </Badge>
                      ))}
                    </Group>
                  </div>

                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Effective Capabilities (including inherited)
                    </Text>
                    <Group gap={4}>
                      {effectiveRole.effectiveCapabilities.map((cap) => (
                        <Badge key={cap} size="sm" color="purple">
                          {cap}
                        </Badge>
                      ))}
                      {effectiveRole.effectiveCapabilities.length === 0 && (
                        <Text size="xs" c="dimmed">
                          No capabilities
                        </Text>
                      )}
                    </Group>
                  </div>

                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Inheritance Chain
                    </Text>
                    <Group gap={4}>
                      {effectiveRole.inheritanceChain.map((parent, index) => (
                        <React.Fragment key={parent}>
                          {index > 0 && <Text size="xs" c="dimmed">→</Text>}
                          <Badge size="sm" color="teal">
                            {parent}
                          </Badge>
                        </React.Fragment>
                      ))}
                      {effectiveRole.inheritanceChain.length === 0 && (
                        <Text size="xs" c="dimmed">
                          No inheritance
                        </Text>
                      )}
                    </Group>
                  </div>
                </Stack>
              </Card>
            )}

            {!effectiveRole && selectedRoleId && (
              <Alert color="gray" icon={<IconAlertCircle size={16} />}>
                Select a role to view its effective permissions
              </Alert>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Role Inheritance Tab */}
        <Tabs.Panel value="inheritance" pt="md">
          <Stack gap="md">
            <Alert color="blue" icon={<IconTree size={16} />}>
              Role inheritance tree showing parent-child relationships. Child roles inherit all
              permissions from their parents.
            </Alert>

            {roles
              .filter((r) => r.inheritsFrom.length === 0) // Root roles only
              .map((role) => (
                <Card key={role.roleId} withBorder p="md">
                  {buildInheritanceTree(role)}
                </Card>
              ))}

            {roles.every((r) => r.inheritsFrom.length > 0) && (
              <Text c="dimmed" ta="center" py="xl">
                No root roles defined (all roles inherit from parents)
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Import Templates Tab */}
        <Tabs.Panel value="templates" pt="md">
          <Stack gap="md">
            <Alert color="green" icon={<IconDownload size={16} />}>
              Import predefined role templates to quickly set up standard roles with recommended
              permissions.
            </Alert>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {availableTemplates.map((template) => (
                <Card key={template.value} withBorder p="md" shadow="sm">
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text fw={700}>{template.label}</Text>
                      <Badge color="blue">{template.scopes.length} scopes</Badge>
                    </Group>

                    <Text size="sm" c="dimmed">
                      {template.description}
                    </Text>

                    <div>
                      <Text size="xs" c="dimmed" mb={4}>
                        Included Scopes:
                      </Text>
                      <Group gap={4}>
                        {template.scopes.slice(0, 3).map((scope) => (
                          <Badge key={scope} size="xs" variant="light">
                            {scope}
                          </Badge>
                        ))}
                        {template.scopes.length > 3 && (
                          <Badge size="xs" variant="light" color="gray">
                            +{template.scopes.length - 3} more
                          </Badge>
                        )}
                      </Group>
                    </div>

                    <Button
                      variant="light"
                      fullWidth
                      onClick={() => {
                        setSelectedTemplate(template.value);
                        setIsImportModalOpen(true);
                      }}
                    >
                      Import Template
                    </Button>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Import Template Confirmation Modal */}
      <Modal
        opened={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title={
          <Group gap="xs">
            <IconDownload size={20} />
            <Text fw={700}>Import Permission Template</Text>
          </Group>
        }
        size="md"
      >
        {selectedTemplate && (
          <Stack gap="md">
            {(() => {
              const template = availableTemplates.find((t) => t.value === selectedTemplate);
              return template ? (
                <>
                  <Alert color="blue" icon={<IconShield size={16} />}>
                    This will create a new role with predefined permissions from the template.
                  </Alert>

                  <Card withBorder p="sm">
                    <Text size="sm" fw={600}>
                      {template.label}
                    </Text>
                    <Text size="sm" c="dimmed" mt="xs">
                      {template.description}
                    </Text>
                    <Text size="xs" c="dimmed" mt="xs">
                      Scopes: {template.scopes.length}
                    </Text>
                  </Card>

                  <Group justify="space-between">
                    <Button variant="subtle" onClick={() => setIsImportModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleImportTemplate} loading={loading}>
                      Import Template
                    </Button>
                  </Group>
                </>
              ) : null;
            })()}
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
